import React from 'react';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { useDashboard } from '../context/DashboardContext';
import { getBalanceTrends, getCategorySpending, formatCurrency, formatMonth, getMonthName } from '../utils/helpers';

function LineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{getMonthName(label)}</p>
      {payload.map((e, i) => (
        <p key={i} className="chart-tooltip__row" style={{ color: e.color }}>
          {e.name}: {formatCurrency(e.value)}
        </p>
      ))}
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{d.name}</p>
      <p className="chart-tooltip__row">{formatCurrency(d.value)}</p>
      <p className="chart-tooltip__meta">
        {((d.value / d.payload.total) * 100).toFixed(1)}%
      </p>
    </div>
  );
}

function Charts() {
  const { transactions } = useDashboard();
  const trends = getBalanceTrends(transactions);
  const spending = getCategorySpending(transactions);
  const total = spending.reduce((s, c) => s + c.amount, 0);
  const pieData = spending.map((c) => ({ ...c, total }));

  return (
    <div className="charts-grid">
      {/* Line Chart */}
      <div className="card">
        <h3 className="card__title">Balance Trends</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trends}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tickFormatter={formatMonth} stroke="#94a3b8" fontSize={12} />
            <YAxis tickFormatter={(v) => `$${v / 1000}k`} stroke="#94a3b8" fontSize={12} />
            <Tooltip content={<LineTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="income"   stroke="#10b981" strokeWidth={2.5} name="Income"   dot={{ r: 4, fill: '#10b981' }} />
            <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2.5} name="Expenses" dot={{ r: 4, fill: '#ef4444' }} />
            <Line type="monotone" dataKey="balance"  stroke="#3b82f6" strokeWidth={2.5} name="Balance"  dot={{ r: 4, fill: '#3b82f6' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Pie Chart */}
      <div className="card">
        <h3 className="card__title">Spending by Category</h3>
        {spending.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%" cy="50%"
                outerRadius={100}
                dataKey="amount"
                nameKey="category"
                label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty">No expense data available</div>
        )}
      </div>
    </div>
  );
}

export default Charts;
